import React, { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { MultiStepForm, Step } from '../../../components/molecules/Form/components/MultiStepForm';
import Information from './partials/Information';
import TeaserImage from './partials/TeaserImage';
import { Namespace } from '../../../translation/namespace';
import { InteractionStatus } from '../../../types/InteractionStatus';
import { FormUtils, useFormContext } from '../../../components/molecules/Form';
import FormsElementContainer, {
  FormsElementContainerModifier,
} from '../../../components/molecules/Form/components/FormsElementContainer/FormsElementContainer';
import Btn from '../../../components/molecules/Btn';
import LiveRegion from '../../../components/atoms/LiveRegion/LiveRegion';
import useRedirect from '../../../hooks/useRedirect';
import { translatePartOfStringWithLink } from '../../../lib/StringUtils/StringHTMLUtils';
import { useAppContext } from '../../../contexts/AppContext/AppContext';
import ModalContext from '../../../components/organisms/ModalDialog/ModalContext';
import { StatusInfoError } from '../../../_lib/components/StatusInfo';
import { TopHeadlineImage, TopHeadlineImageSmall } from '../../../types/api/ImageType';
import { FormType } from './TopHeadlinesForm';
import { useBlockContext } from '../../../_lib/BlockFetcher';
import { BlockType } from '../../../types/api/ElementType';
import { RequiredHelpPhrase } from '../../../_lib/Form2';

type TopHeadlinesFormTemplate = {
  type: FormType;
  image?: TopHeadlineImage | TopHeadlineImageSmall;
};

export default function TopHeadlinesFormTemplate({ type, image }: TopHeadlinesFormTemplate) {
  const { t } = useTranslation(Namespace.TopHeadlinesForm);

  const { inputs, status, submitActionEnabled, getStatus, responseUrl } = useFormContext();
  const { daterange } = inputs;
  const { preventModalClose, closePreventedMessage } = useContext(ModalContext);
  const { portalConfiguration } = useAppContext();
  const { elementType } = useBlockContext();
  const [startRedirect] = useRedirect();

  const getLinkedForwardMessage = () => {
    if (daterange.valueChanged()) {
      const todayFormatted = dayjs().format('YYYY-MM-DD');
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const endDateOfTopHeadline = daterange.params.endDate.value;
      if (elementType === BlockType.pagemanagement && todayFormatted > endDateOfTopHeadline) {
        return translatePartOfStringWithLink(
          `${Namespace.TopHeadlinesForm}:status.create.successForwardArchive`,
          responseUrl,
          false,
        );
      }
      if (elementType === BlockType.topheadlinesarchive && todayFormatted < endDateOfTopHeadline) {
        return translatePartOfStringWithLink(
          `${Namespace.TopHeadlinesForm}:status.create.successForwardTopHeadlines`,
          responseUrl,
          false,
        );
      }
      if (elementType === BlockType.topheadlines && todayFormatted > endDateOfTopHeadline) {
        return translatePartOfStringWithLink(
          `${Namespace.TopHeadlinesForm}:status.create.successForwardArchive`,
          responseUrl,
          false,
        );
      }
    }

    return translatePartOfStringWithLink(
      `${Namespace.TopHeadlinesForm}:status.create.successForwardUpdate`,
      responseUrl,
      false,
    );
  };

  const statusMessages = {
    [InteractionStatus.SUCCESS]: [
      FormUtils.createStatusMessage(t('status.create.success'), InteractionStatus.SUCCESS),
      FormUtils.createStatusMessage(getLinkedForwardMessage(), InteractionStatus.SENDING),
    ],
    [InteractionStatus.SENDING]: FormUtils.createStatusMessage(t('status.create.sending'), InteractionStatus.SENDING),
    [InteractionStatus.FAIL]: FormUtils.createStatusMessage(
      translatePartOfStringWithLink(
        `${Namespace.TopHeadlinesForm}:status.create.fail`,
        portalConfiguration.son_portal_support_email && `mailto:${portalConfiguration.son_portal_support_email}`,
      ),
    ),
  };

  useEffect(() => {
    if (status === InteractionStatus.SENDING) {
      preventModalClose(true);
    }

    if (status === InteractionStatus.SUCCESS) {
      startRedirect(responseUrl);
    }

    if (status === InteractionStatus.FAIL) {
      preventModalClose(false);
    }
  }, [startRedirect, preventModalClose, status, responseUrl]);

  const renderSubmitAndLiveRegion = (
    <>
      <FormsElementContainer modifier={FormsElementContainerModifier.BUTTON_GROUP_RIGHT}>
        <Btn
          cssClass="btn--single-action btn--single-action-yes forms__button"
          text={t(`saveButton.${type}`)}
          ariaDisabled={!submitActionEnabled()}
          type="submit"
        />
      </FormsElementContainer>
      <LiveRegion invisible={false} htmlTag="div">
        {getStatus(statusMessages)}
        {closePreventedMessage && <StatusInfoError message={closePreventedMessage} modifier="right" />}
      </LiveRegion>
    </>
  );

  return (
    <MultiStepForm initialActiveTab="top-headlines-form-informations">
      <Step id="top-headlines-form-informations" buttonLabel={t('steps.informations')}>
        <RequiredHelpPhrase />
        <Information />
      </Step>
      <Step
        id="top-headlines-form-teaserimage"
        buttonLabel={t('steps.teaserimage')}
        renderSubmitAndLiveRegion={renderSubmitAndLiveRegion}
      >
        <TeaserImage initialImage={image} />
      </Step>
    </MultiStepForm>
  );
}
